import React, { FC, useEffect, useState, useRef } from "react";
import "./SpinningCircleLoader.css"; // Import the CSS file for styling
import { AppHome } from "./AppHome";
import { AppProps } from "../types";
import { AppHeader } from "./header/AppHeader";
// const backendURI = 'backend-container:3000';
// const backendURI = 'localhost:3000';
// const backendURI = '18.219.96.136/API';
import MainInterface from "./Interface";
import { AppContext, AppState } from "./context/AppContext";
import { io, Socket } from "socket.io-client";
import { applyDelta, OrgDeltaPayload } from "./applyDelta";
import { Toaster, enqueueToaster, dequeueToaster } from "@deere/isg.component-library";
import type { ToasterKey } from "@deere/isg.component-library";
import { setOrgContext, reportSocketPing, reportHighLatencyGoogleProbe } from "../datadog-rum";
import { checkInternetConnectivity } from "./checkInternetConnectivity";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;
const socketURI = process.env.REACT_APP_SOCKET_URI;
const persistentHeaderURI = process.env.REACT_APP_PERSISTENT_HEADER_URI;

export const App: FC<AppProps> = (props: AppProps) => {
  const [mqttData, setMqttData] = useState({});
  const appContext: AppState = React.useContext(AppContext);
  const [orgName, setOrgName] = useState<string>(appContext.selectedOrganization?.name || "");
  const orgNameRef = useRef(orgName);
  const oldOrgIdRef = useRef<string | null>(null);
  const mqttFlushHandleRef = useRef<number | null>(null);
  // Authoritative working copy of the org state. Deltas are applied here
  // synchronously (with structural sharing) and pushed to React once per frame.
  const mqttWorkingRef = useRef<Record<string, any>>({});
  // Per-org last applied broadcast sequence, for gap detection.
  const lastSeqRef = useRef<Map<string, number>>(new Map());
  // Debounced resync timer (per gap), keyed by the org needing a resync.
  const resyncTimerRef = useRef<number | null>(null);
  const pendingResyncOrgRef = useRef<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [wsConnected, setWsConnected] = useState<boolean>(false);
  const [latency, setLatency] = useState<number>(0);
  const hasConnectedRef = useRef(false);
  const disconnectToastKeyRef = useRef<ToasterKey | null>(null);
  const highLatencyToastKeyRef = useRef<ToasterKey | null>(null);
  // Tracks the timeout before the next allowed internet-connectivity check
  // so we don't flood Google on every ping cycle while latency is elevated.
  const connectivityCheckCooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Manage persistent disconnection toast
  useEffect(() => {
    if (wsConnected) {
      hasConnectedRef.current = true;
      if (disconnectToastKeyRef.current !== null) {
        dequeueToaster(disconnectToastKeyRef.current);
        disconnectToastKeyRef.current = null;
      }
    } else if (hasConnectedRef.current) {
      // Only show disconnect toast after we've connected at least once
      if (disconnectToastKeyRef.current === null) {
        disconnectToastKeyRef.current = enqueueToaster(
          <>
            Error: server disconnected
            <button
              onClick={() => window.location.reload()}
              style={{
                marginLeft: "12px",
                padding: "4px 12px",
                cursor: "pointer",
                background: "white",
                color: "#d32f2f",
                border: "1px solid #d32f2f",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              Reload
            </button>
          </>,
          "Connection Lost",
          {
            variant: "error",
            isPersistent: true,
            isCloseable: false,
          },
        );
      }
    }
  }, [wsConnected]);

  useEffect(() => {
    if (socket) {
      const interval = setInterval(() => {
        const start = Date.now();
        socket.emit("pingLatency", (ack: { serverReceivedAt?: number; serverSentAt?: number }) => {
          const received = Date.now();
          const duration = received - start;
          setLatency(duration);
          socket.emit("latencyUpdate", { latency: duration });
          reportSocketPing(start, received, ack?.serverReceivedAt, ack?.serverSentAt);

          if (duration > 2000 && connectivityCheckCooldownRef.current === null) {
            // Cooldown prevents repeated checks while latency stays elevated.
            connectivityCheckCooldownRef.current = setTimeout(() => {
              connectivityCheckCooldownRef.current = null;
            }, 30_000);

            checkInternetConnectivity().then((result) => {
              reportHighLatencyGoogleProbe(duration, result.reachable, result.rtt);
            });
          }
        });
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [socket]);

  useEffect(() => {
    orgNameRef.current = orgName;
  }, [orgName]);

  useEffect(() => {
    // The backend URI should point to your WebSocket server
    // note that from docker compose the variable is set as backendURI = "orchestrator-local.deere.com"
    // Ensure the URI has a protocol - use https:// for production, http:// for local dev
    let socketUri = socketURI || "http://localhost:3001";
    if (socketUri && !socketUri.startsWith("http://") && !socketUri.startsWith("https://")) {
      socketUri = `https://${socketUri}`;
    }
    console.log("Connecting to socket URI: ", socketUri);
    console.log("User ID: ", props.user.userId);
    const newSocket = io(socketUri, {
      withCredentials: true,
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query: {
        userId: props.user.userId,
        userName: props.user.name,
        orgId: orgNameRef.current,
      },
    });

    console.log(" Query: ", JSON.parse(JSON.stringify(newSocket.io.opts.query)));

    setSocket(newSocket);

    // newSocket.onAny((event, ...args) => {
    //     console.log(`Socket event: ${event}`, args);
    // });

    newSocket.on("connect", () => {
      console.log("on Connect : Connected to WebSocket server");
      setWsConnected(true);
      const currentOrg = orgNameRef.current?.trim();
      // Empty / no-selection is a normal startup state (org not loaded yet),
      // not an attack: skip silently. Deere org names can contain spaces and
      // "&"; the backend canAccessOrg() check is the security boundary.
      if (currentOrg) {
        newSocket.emit("requestInitialData", { orgId: currentOrg, oldOrgId: "" });
      }

      // Send location data
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const { latitude, longitude } = position.coords;
          newSocket.emit("updateLocation", { lat: latitude, lng: longitude });
        });

        // Watch for location changes
        navigator.geolocation.watchPosition((position) => {
          const { latitude, longitude } = position.coords;
          newSocket.emit("updateLocation", { lat: latitude, lng: longitude });
        });
      }
    });

    // Coalesce bursty server emits into one React state update per animation
    // frame. Without this, a tight backend flush cycle can outrun React's
    // commit pipeline and trip "Maximum update depth exceeded" downstream.
    const scheduleFlush = () => {
      if (mqttFlushHandleRef.current !== null) return;
      mqttFlushHandleRef.current = requestAnimationFrame(() => {
        mqttFlushHandleRef.current = null;
        setMqttData(mqttWorkingRef.current);
      });
    };

    // Debounced resync: on a detected sequence gap we ask the server for a
    // fresh full snapshot instead of applying a delta onto a stale baseline.
    const scheduleResync = (orgId: string) => {
      pendingResyncOrgRef.current = orgId;
      if (resyncTimerRef.current !== null) return;
      resyncTimerRef.current = window.setTimeout(() => {
        resyncTimerRef.current = null;
        const target = pendingResyncOrgRef.current;
        pendingResyncOrgRef.current = null;
        // oldOrgId "" => server only (idempotently) re-joins the room and
        // re-sends the snapshot; we never leave the live room.
        if (target) {
          newSocket.emit("requestInitialData", { orgId: target, oldOrgId: "" });
        }
      }, 250);
    };

    newSocket.on("initialData", (payload: any) => {
      // New shape: { data, seq }. Tolerate a legacy bare-object payload too.
      const data =
        payload && typeof payload === "object" && "data" in payload ? payload.data : payload;
      const seq =
        payload && typeof payload === "object" && typeof payload.seq === "number" ? payload.seq : 0;
      if (!data || typeof data !== "object") return;
      // Authoritative baseline for the requested org. Replace the working copy
      // wholesale (drops stale org keys from a prior view) and re-baseline seq.
      mqttWorkingRef.current = data;
      const orgId = Object.keys(data)[0];
      if (orgId) lastSeqRef.current.set(orgId, seq);
      if (resyncTimerRef.current !== null) {
        clearTimeout(resyncTimerRef.current);
        resyncTimerRef.current = null;
        pendingResyncOrgRef.current = null;
      }
      scheduleFlush();
    });

    newSocket.on("dataDelta", (payload: OrgDeltaPayload) => {
      if (!payload || typeof payload !== "object" || !Array.isArray(payload.patches)) return;
      const { orgId, seq, reset } = payload;
      if (!orgId) return;

      if (reset) {
        // Full authoritative snapshot — accept unconditionally and re-baseline.
        mqttWorkingRef.current = applyDelta(mqttWorkingRef.current, payload);
        lastSeqRef.current.set(orgId, seq);
        if (pendingResyncOrgRef.current === orgId && resyncTimerRef.current !== null) {
          clearTimeout(resyncTimerRef.current);
          resyncTimerRef.current = null;
          pendingResyncOrgRef.current = null;
        }
        scheduleFlush();
        return;
      }

      const lastSeq = lastSeqRef.current.get(orgId);
      if (lastSeq === undefined) {
        // Delta arrived before any baseline — ask for a snapshot.
        scheduleResync(orgId);
        return;
      }
      if (seq <= lastSeq) return; // duplicate / out-of-order straggler
      if (seq !== lastSeq + 1) {
        // Missed one or more deltas: resync rather than diverge silently.
        scheduleResync(orgId);
        return;
      }

      mqttWorkingRef.current = applyDelta(mqttWorkingRef.current, payload);
      lastSeqRef.current.set(orgId, seq);
      scheduleFlush();
    });

    newSocket.on("disconnect", () => {
      console.log("Disconnected from WebSocket server");
      setWsConnected(false);
    });

    newSocket.on("highLatencyWarning", (data: { latency: number; threshold: number }) => {
      // Show a persistent toast while the operator's connection is degraded.
      // We do NOT exit cab control — the operator chooses how to respond.
      if (highLatencyToastKeyRef.current === null) {
        highLatencyToastKeyRef.current = enqueueToaster(
          `Your connection latency is above ${data.threshold}ms (currently ${data.latency}ms). Cab control heartbeat is paused until your connection recovers.`,
          "High Latency",
          {
            variant: "warning",
            isPersistent: true,
            isCloseable: false,
          },
        );
      }
    });

    newSocket.on("highLatencyCleared", () => {
      if (highLatencyToastKeyRef.current !== null) {
        dequeueToaster(highLatencyToastKeyRef.current);
        highLatencyToastKeyRef.current = null;
      }
    });

    newSocket.on(
      "commandNotification",
      (data: { username: string; commandType: string; machineName: string }) => {
        const commandPhrases: Record<string, string> = {
          START: "started autonomy on",
          PAUSE: "paused autonomy on",
          RESUME: "resumed autonomy on",
          QUIT: "quit the mission on",
          CANCEL: "cancelled Start Autonomy on",
          ALLSTOP: "issued All Stop on",
        };
        const actionPhrase = commandPhrases[data.commandType] || `issued ${data.commandType} on`;
        const machineLabel = data.machineName?.trim() || "a machine";
        const userName = data.username?.trim() || "A user";
        enqueueToaster(`${userName} ${actionPhrase} ${machineLabel}`, undefined, {
          variant: "info",
        });
      },
    );

    newSocket.on("commandAutoCancelled", (data: { machineName: string }) => {
      const machineLabel = data.machineName?.trim() || "the machine";
      enqueueToaster(
        `Your Start Autonomy on ${machineLabel} timed out and was cancelled — the machine never became active.`,
        undefined,
        { variant: "warning" },
      );
    });

    newSocket.on("connect_error", (err) => {
      console.error("Connection error:", err);
      if (err.message === "Authentication required") {
        // Abort reconnection — retrying won't help without a valid session
        newSocket.io.opts.reconnection = false;
        newSocket.disconnect();
        window.location.href = "/API/auth/login";
      }
    });

    // Surface server-side authorization/validation errors to the operator.
    // The backend emits an "error" event (e.g. an org the user isn't a
    // member of) when canAccessOrg() denies an org request; without this
    // handler the request silently fails and the user sees nothing.
    newSocket.on("error", (payload: { message?: string }) => {
      const message =
        payload?.message ||
        "You don't have access to this organization. Please choose a different one.";
      console.error("Server error:", message);
      enqueueToaster(message, "Organization Access Denied", { variant: "error" });
    });

    return () => {
      // console.log('Disconnecting from WebSocket server');
      if (mqttFlushHandleRef.current !== null) {
        cancelAnimationFrame(mqttFlushHandleRef.current);
        mqttFlushHandleRef.current = null;
      }
      if (resyncTimerRef.current !== null) {
        clearTimeout(resyncTimerRef.current);
        resyncTimerRef.current = null;
      }
      pendingResyncOrgRef.current = null;
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    const trimmedOrg = orgName?.trim();
    if (socket && trimmedOrg) {
      // Store the previous orgId before updating
      const oldOrgId = oldOrgIdRef.current || "";

      // Deere org names may contain spaces and "&" (e.g. "C&F",
      // "Files User Org"). We deliberately do NOT format-gate the org id here;
      // the backend canAccessOrg() check is the authoritative security boundary.
      oldOrgIdRef.current = trimmedOrg;

      console.log(`Organization changed from: ${oldOrgId} to: ${trimmedOrg}. Requesting new data.`);
      setOrgContext(trimmedOrg);
      socket.emit("requestInitialData", { orgId: trimmedOrg, oldOrgId });
    }
  }, [orgName, socket]);

  return (
    <AppHome translations={undefined}>
      <AppHeader
        persistentHeaderUrl={
          process.env.REACT_APP_PERSISTENT_HEADER_URI || "https://persistent-header-qual.deere.com"
        }
        // persistentHeaderUrl={persistentHeaderURI ? persistentHeaderURI : 'https://persistent-header-qual.deere.com'}
        userId={props.user.userId}
        socket={socket}
        latency={latency}
        user={props.user}
      >
        <>
          <Toaster
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            autoHideDuration={3000}
            maxSnack={5}
            classes={{ containerAnchorOriginTopRight: "toast-container-override" }}
          />

          <MainInterface
            userName={props.user.name}
            fullMQTT={mqttData}
            orgName={orgName}
            setOrgName={setOrgName}
          />
        </>
      </AppHeader>
    </AppHome>
  );
};
